/**
 * Viewer interaction preferences — reduced motion and the pointer cursor —
 * applied to `<html>` as data attributes the stylesheet and the JS motion
 * hooks both read.
 *
 * Motion is a tri-state because the OS already has an opinion: "system"
 * follows `prefers-reduced-motion`, "on" reduces regardless of it, and "off"
 * keeps motion even when the OS asks for less. The attribute carries only the
 * two overrides — "system" REMOVES it, so an untouched document falls through
 * to the media query alone, which is also what every consumer that never
 * calls these appliers gets.
 *
 * The CSS side lives in `index.css`: the `motion-reduce` / `motion-safe`
 * variants are redefined there to fold the attribute into the media query,
 * so every existing `motion-reduce:` utility honors the override with no
 * per-call-site work.
 */

export const MOTION_PREFERENCES = ["system", "on", "off"] as const;

export type MotionPreference = (typeof MOTION_PREFERENCES)[number];

export function isMotionPreference(
  value: string | null | undefined,
): value is MotionPreference {
  return (MOTION_PREFERENCES as readonly string[]).includes(value ?? "");
}

/**
 * Coerces a stored motion preference. Preferences travel as untyped strings
 * (null while unloaded, arbitrary if the stored row was ever hand-edited), so
 * anything unknown resolves to "system" — the behavior an untouched document
 * already has.
 */
export function resolveMotionPreference(
  value: string | null | undefined,
): MotionPreference {
  return isMotionPreference(value) ? value : "system";
}

const REDUCE_MOTION_ATTR = "data-reduce-motion";
const POINTER_CURSORS_ATTR = "data-pointer-cursors";

/** Writes the motion preference to `<html>`; "system" removes the attribute. */
export function applyMotionPreference(preference: MotionPreference): void {
  const root = document.documentElement;
  if (preference === "system") root.removeAttribute(REDUCE_MOTION_ATTR);
  else root.setAttribute(REDUCE_MOTION_ATTR, preference);
}

/**
 * Whether a forced motion preference is on the document — `null` when the
 * viewer follows the OS. The imperative read for code that asks at the moment
 * it animates (WAAPI cores); reactive consumers use `usePrefersReducedMotion`.
 */
export function forcedReducedMotion(): boolean | null {
  const value = document.documentElement.getAttribute(REDUCE_MOTION_ATTR);
  return value === "on" ? true : value === "off" ? false : null;
}

/**
 * Writes the pointer-cursor preference to `<html>`. ON is the shipped default
 * and removes the attribute; OFF stamps it, and the stylesheet's override
 * rule turns the interactive pointer into the default arrow.
 */
export function applyPointerCursorPreference(enabled: boolean): void {
  const root = document.documentElement;
  if (enabled) root.removeAttribute(POINTER_CURSORS_ATTR);
  else root.setAttribute(POINTER_CURSORS_ATTR, "off");
}
